import React, { useRef, useState } from 'react';
import { Building2, Server, Shield, HardHat, Factory, Radio, Zap, Settings, MoveHorizontal } from 'lucide-react';

const INFRASTRUCTURE = [
  { name: "Alarm- & Einsatzzentralen", icon: <Radio className="w-5 h-5" /> },
  { name: "Bahntechnik", icon: <Settings className="w-5 h-5" /> },
  { name: "Bahnsteuerungen", icon: <Zap className="w-5 h-5" /> },
  { name: "Banken", icon: <Shield className="w-5 h-5" /> },
  { name: "Betonwerken", icon: <Factory className="w-5 h-5" /> },
  { name: "Datacenter", icon: <Server className="w-5 h-5" /> },
  { name: "Fabrikationsanlagen", icon: <Factory className="w-5 h-5" /> },
  { name: "Fensterfabriken", icon: <Factory className="w-5 h-5" /> },
  { name: "Gefängnissen", icon: <Shield className="w-5 h-5" /> },
  { name: "Kehrichtverbrennungsanlagen", icon: <Factory className="w-5 h-5" /> },
  { name: "Kiesaufbereitungsanlagen", icon: <Factory className="w-5 h-5" /> },
  { name: "Kläranlagen", icon: <Settings className="w-5 h-5" /> },
  { name: "Kommunikationsanlagen", icon: <Radio className="w-5 h-5" /> },
  { name: "Leitstände", icon: <Building2 className="w-5 h-5" /> },
  { name: "Logistikzentren", icon: <Building2 className="w-5 h-5" /> },
  { name: "Rechenzentren", icon: <Server className="w-5 h-5" /> },
  { name: "Seniorenheimen", icon: <Building2 className="w-5 h-5" /> },
  { name: "Spitälern", icon: <Building2 className="w-5 h-5" /> },
  { name: "Strassenüberwachungs-Systeme", icon: <Radio className="w-5 h-5" /> },
  { name: "Telefonzentralen", icon: <Radio className="w-5 h-5" /> },
  { name: "Tunnelanlagen", icon: <HardHat className="w-5 h-5" /> },
  { name: "Verteilerzentralen", icon: <Building2 className="w-5 h-5" /> },
  { name: "Verwaltung etc.", icon: <Building2 className="w-5 h-5" /> }
];

const LOCATIONS = [
  "Aarau", "Andermatt", "Arth-Goldau", "Aarburg", "Basel", "Basel-Stadt", "Bern", "Bellinzona", "Biel/Bienne", 
  "Birmensdorf", "Carouge", "Chur", "Dietikon", "Dottikon", "Dielsdorf", "Dübendorf", "Freiburg", "Ermatingen", 
  "Glattbrugg", "Grenchen", "Hägendorf", "Herisau", "Ittigen", "Kloten", "Köniz", "Lachen", "Lupfig", "Luzern", 
  "Männedorf", "Meyrin", "Mörschwil", "Meilen", "Neuchâtel", "Niederbipp", "Niederbuchsiten", "Neckarsulm", 
  "Oerlikon", "Oftringen", "Olten", "Ostermundigen", "Pfäffikon", "Pratteln", "Reinach", "Rheinfelden", "Schinznach", 
  "Schönbühl", "Solothurn", "Sindelfingen", "Spreitenbach", "Steinen", "Sedrun", "Stuttgart", "Spiez", "Thalwil", 
  "Therwil", "Thurgau", "Thun", "Urdorf", "Uster", "Wallisellen", "Wädenswil", "Wetzikon", "Wettingen", "Wien", 
  "Winterthur", "Zofingen", "Zollikofen", "Zug", "Zürich uva."
];

const TrustBar: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div className="w-full border-y border-divider bg-surface overflow-hidden py-10 md:py-16 select-none">
      <div className="max-w-content mx-auto px-5 mb-8 md:mb-12 text-center">
        <h2 className="text-xl md:text-3xl font-black text-primary mb-2 leading-tight">
          Schutz für kritische Infrastruktur.
        </h2>
        <p className="text-gray-500 text-xs md:text-sm max-w-2xl mx-auto leading-relaxed">
          Spezialisierte Reinigung techn-elektronischer Umgebungen in der gesamten Schweiz.
        </p>
      </div>

      <div className="relative group mb-6 overflow-visible">
        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-surface to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-l from-surface to-transparent z-10 pointer-events-none"></div>
        
        <div 
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          className={`flex overflow-x-auto gap-4 md:gap-5 px-12 md:px-24 pb-6 no-scrollbar snap-x snap-mandatory ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} transition-all duration-150`}
        >
          {INFRASTRUCTURE.map((item, i) => (
            <div 
              key={i} 
              className="flex-none w-56 md:w-72 snap-center bg-white border border-divider rounded-xl md:rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all relative"
            >
              <div className="h-16 md:h-20 bg-accent/5 flex items-center justify-center relative overflow-hidden">
                 <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-accent border border-accent flex items-center justify-center text-white shadow-lg group-hover/card:scale-105 transition-transform">
                    {item.icon}
                 </div>
              </div>
              <div className="p-4 md:p-6">
                <h3 className="font-bold text-primary text-xs md:text-base leading-tight">
                    {item.name}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center items-center gap-3 mb-10 opacity-30">
          <div className="w-8 h-px bg-divider"></div>
          <MoveHorizontal className="w-4 h-4 text-accent animate-pulse" />
          <div className="w-8 h-px bg-divider"></div>
      </div>

      <div className="max-w-content mx-auto px-5 mb-3">
          <p className="text-center text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-3">Präsenz an über 70 Standorten:</p>
      </div>

      <div className="relative opacity-40 grayscale overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          <div className="flex gap-8 md:gap-12 items-center">
            {LOCATIONS.concat(LOCATIONS).map((city, i) => (
              <span key={i} className="text-[11px] md:text-sm font-medium text-gray-400 italic">
                {city}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default TrustBar;